'use client'

import Responsive from '@lyra/core/components/Responsive'
import { InstrumentType } from '@lyra/web/constants/instruments'
import {
  MAX_PAGE_WIDTH,
  POSITIONS_DRAWER_MIN_HEIGHT,
  TRADE_BODY_HEIGHT_DESTKOP,
  TRADE_HEADER_MARKET_SECTION_HEIGHT,
  TRADE_RIGHT_RAIL_WIDTH_DESKTOP,
} from '@lyra/web/constants/layout'
import { MarketId } from '@lyra/web/constants/markets'
import { CollateralId, CurrencyId } from '@lyra/web/constants/tokens'
import PositionsSection from '@lyra/web/containers/common/PositionsSection'
import MarketHeaderSections from '@lyra/web/containers/trade/MarketHeaderSections'
import TradeFormOrderbookQueryParams from '@lyra/web/containers/trade/TradeFormOrderbookQueryParams'
import TradeFormQueryParams from '@lyra/web/containers/trade/TradeFormQueryParams'
import TradeSubaccountSection from '@lyra/web/containers/trade/TradeSubaccountSection'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getPerpInstrumentName, getSpotInstrumentName } from '@lyra/web/utils/instruments'
import { getCurrencyId } from '@lyra/web/utils/tokens'
import { coerce } from '@lyra/web/utils/types'
import { notFound, usePathname } from 'next/navigation'
import React, { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

type Props = {
  children: React.ReactNode
}

const TradeForm = ({
  currencyId,
  instrumentType,
  noTopBorder,
}: {
  currencyId: CurrencyId
  instrumentType: InstrumentType
  noTopBorder?: boolean
}) => {
  const marketId = coerce(MarketId, currencyId)
  const collateralId = coerce(CollateralId, currencyId)
  switch (instrumentType) {
    case InstrumentType.Options:
      return <TradeFormQueryParams noTopBorder={noTopBorder} />
    case InstrumentType.Perps:
      return marketId ? (
        <TradeFormOrderbookQueryParams
          noTopBorder={noTopBorder}
          instrumentName={getPerpInstrumentName(marketId)}
        />
      ) : null
    case InstrumentType.Spot:
      return collateralId ? (
        <TradeFormOrderbookQueryParams
          noTopBorder={noTopBorder}
          instrumentName={getSpotInstrumentName(collateralId)}
        />
      ) : null
  }
}

const TradeLayoutHelper = ({ children }: Props) => {
  const { subaccounts } = useSubaccount()
  const hasMultipleSubaccounts = Object.values(subaccounts).length >= 2

  const pathname = usePathname()
  const { instrumentType, currencyId } = useMemo(() => {
    const [_0, part1, part2] = pathname.toLowerCase().split('/')
    const instrumentType =
      part1 === 'perps'
        ? InstrumentType.Perps
        : part1 === 'options'
        ? InstrumentType.Options
        : part1 === 'spot'
        ? InstrumentType.Spot
        : null

    const currencyId = getCurrencyId(part2)
    return {
      instrumentType,
      currencyId,
    }
  }, [pathname])

  if (!instrumentType || !currencyId) {
    return notFound()
  }

  return (
    <Responsive
      width="100%"
      $desktop={{
        flexGrow: 1,
        flexShrink: 1,
        alignItems: 'center',
        overflow: 'scroll',
      }}
      desktop={
        <XStack flexGrow={1} flexShrink={1} maxWidth={MAX_PAGE_WIDTH} width="100%">
          <YStack flexGrow={1} flexShrink={1} borderColor="$hairline" borderRightWidth={1}>
            <MarketHeaderSections
              height={TRADE_HEADER_MARKET_SECTION_HEIGHT}
              currencyId={currencyId}
              instrumentType={instrumentType}
            />
            {/* limit trade body height */}
            <YStack height={TRADE_BODY_HEIGHT_DESTKOP}>{children}</YStack>
            <PositionsSection
              isCompact
              showSubaccountSummary
              minHeight={POSITIONS_DRAWER_MIN_HEIGHT}
              pageSize={10}
            />
          </YStack>
          <YStack width={TRADE_RIGHT_RAIL_WIDTH_DESKTOP} id="trade-right-rail">
            {hasMultipleSubaccounts ? (
              <TradeSubaccountSection height={TRADE_HEADER_MARKET_SECTION_HEIGHT} noTopBorder />
            ) : null}
            <TradeForm
              currencyId={currencyId}
              instrumentType={instrumentType}
              noTopBorder={!hasMultipleSubaccounts}
            />
          </YStack>
        </XStack>
      }
      mobile={
        <>
          <MarketHeaderSections currencyId={currencyId} instrumentType={instrumentType} />
          {children}
        </>
      }
    />
  )
}

export default TradeLayoutHelper
